import React, { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import client from "@/api/client";
import { toast } from "sonner";
import { User, Building2, Users, Lock, CheckCircle2 } from "lucide-react";

import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
export default function Settings() {
  const { user, currentOrg } = useAuth();
  const [activeTab, setActiveTab] = useState("profile");
  const [profile, setProfile] = useState({ first_name: user?.first_name || "", last_name: user?.last_name || "" });
  const [passwordForm, setPasswordForm] = useState({ current_password: "", new_password: "" });
  const [orgForm, setOrgForm] = useState({ name: "", sector: "", country: "", website: "", description: "" });
  const [members, setMembers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");

  useEffect(() => {
    if (currentOrg) {
      setOrgForm({
        name: currentOrg.name || "", sector: currentOrg.sector || "",
        country: currentOrg.country || "", website: currentOrg.website || "",
        description: currentOrg.description || ""
      });
      loadMembers();
    }
  }, [currentOrg]);

  const loadMembers = async () => {
    try {
      const res = await client.get(`/organizations/${currentOrg?.id}/members`);
      setMembers(res.data);
    } catch {}
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await client.put("/auth/me", profile);
      toast.success("Profile updated");
    } catch { toast.error("Failed to update profile"); }
    setSaving(false);
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await client.post("/auth/change-password", passwordForm);
      toast.success("Password changed");
      setPasswordForm({ current_password: "", new_password: "" });
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to change password"); }
    setSaving(false);
  };

  const saveOrg = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await client.put(`/organizations/${currentOrg?.id}`, orgForm);
      toast.success("Organization updated");
    } catch { toast.error("Failed to update organization"); }
    setSaving(false);
  };

  const inviteMember = async (e) => {
    e.preventDefault();
    try {
      await client.post(`/organizations/${currentOrg?.id}/members`, { email: inviteEmail, role: "member" });
      toast.success("Member added");
      setInviteEmail("");
      loadMembers();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to add member"); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6 animate-fade-in">
        
      <PageHeader
        title="Settings"
        subtitle="Manage your profile, organization, and team."
      />

      <div className="flex gap-0 border-b border-slate-200">
        {[["profile", "Profile"], ["organization", "Organization"], ["team", "Team"], ["security", "Security"]].map(([key, label]) => (
          <button key={key} onClick={() => setActiveTab(key)} className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${activeTab === key ? "border-[#059669] text-[#059669]" : "border-transparent text-slate-500 hover:text-slate-800"}`} data-testid={`settings-tab-${key}`}>
            {label}
          </button>
        ))}
      </div>

      {activeTab === "profile" && (
        <div className="max-w-lg bg-white border border-slate-200 rounded-xl p-6">
          <h2 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2"><User size={15} /> Profile Information</h2>
          <form onSubmit={saveProfile} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">First Name</label>
                <input type="text" value={profile.first_name} onChange={e => setProfile(f => ({ ...f, first_name: e.target.value }))} className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#059669]" data-testid="profile-firstname" />
              </div>
              <div>
                <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">Last Name</label>
                <input type="text" value={profile.last_name} onChange={e => setProfile(f => ({ ...f, last_name: e.target.value }))} className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#059669]" data-testid="profile-lastname" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">Email</label>
              <input type="email" value={user?.email || ""} disabled className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm bg-slate-50 text-slate-400 cursor-not-allowed" />
            </div>
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">Role</label>
              <input type="text" value={user?.role || ""} disabled className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm bg-slate-50 text-slate-400 cursor-not-allowed capitalize" />
            </div>
            <button type="submit" disabled={saving} className="px-5 py-2 bg-[#0F172A] text-white text-sm font-medium rounded-lg hover:bg-[#1E293B] disabled:opacity-60" data-testid="save-profile-btn">
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </form>
        </div>
      )}

      {activeTab === "organization" && (
        <div className="max-w-lg bg-white border border-slate-200 rounded-xl p-6">
          <h2 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2"><Building2 size={15} /> Organization Details</h2>
          {!currentOrg ? (
            <p className="text-sm text-slate-500">No organization set up yet.</p>
          ) : (
            <form onSubmit={saveOrg} className="space-y-4">
              <div>
                <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">Organization Name</label>
                <input type="text" value={orgForm.name} onChange={e => setOrgForm(f => ({ ...f, name: e.target.value }))} className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#059669]" data-testid="org-name-settings" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">Sector</label>
                  <input type="text" value={orgForm.sector} onChange={e => setOrgForm(f => ({ ...f, sector: e.target.value }))} className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#059669]" />
                </div>
                <div>
                  <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">Country</label>
                  <input type="text" value={orgForm.country} onChange={e => setOrgForm(f => ({ ...f, country: e.target.value }))} className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#059669]" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">Website</label>
                <input type="text" value={orgForm.website} onChange={e => setOrgForm(f => ({ ...f, website: e.target.value }))} className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#059669]" />
              </div>
              <div>
                <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">Description</label>
                <textarea value={orgForm.description} onChange={e => setOrgForm(f => ({ ...f, description: e.target.value }))} className="w-full p-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#059669] resize-none h-20" />
              </div>
              <button type="submit" disabled={saving} className="px-5 py-2 bg-[#0F172A] text-white text-sm font-medium rounded-lg hover:bg-[#1E293B] disabled:opacity-60" data-testid="save-org-btn">
                {saving ? "Saving..." : "Save Organization"}
              </button>
            </form>
          )}
        </div>
      )}

      {activeTab === "team" && (
        <div className="max-w-2xl space-y-4">
          <SectionCard>

            <h2 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2"><Users size={15} /> Team Members</h2>
            <div className="space-y-2 mb-6">
              {members.map(m => (
                <div key={m.membership_id} className="flex items-center justify-between p-3 border border-slate-100 rounded-lg">
                  <div>
                    <p className="text-sm font-medium">{m.first_name} {m.last_name}</p>
                    <p className="text-xs text-slate-400">{m.email}</p>
                  </div>
                  <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full capitalize">{m.role}</span>
                </div>
              ))}
              {members.length === 0 && <p className="text-sm text-slate-400">No team members yet.</p>}
            </div>
            <form onSubmit={inviteMember} className="flex gap-3">
              <input type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="colleague@organization.com" className="flex-1 h-10 px-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#059669]" required data-testid="invite-email-input" />
              <button type="submit" className="px-4 py-2 bg-[#059669] text-white text-sm font-medium rounded-lg hover:bg-emerald-700" data-testid="invite-btn">Invite</button>
            </form>
          
          </SectionCard>
        </div>
      )}

      {activeTab === "security" && (
        <div className="max-w-lg bg-white border border-slate-200 rounded-xl p-6">
          <h2 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2"><Lock size={15} /> Change Password</h2>
          <form onSubmit={changePassword} className="space-y-4">
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">Current Password</label>
              <input type="password" value={passwordForm.current_password} onChange={e => setPasswordForm(f => ({ ...f, current_password: e.target.value }))} required className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#059669]" data-testid="current-password-input" />
            </div>
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">New Password (min. 8 characters)</label>
              <input type="password" value={passwordForm.new_password} onChange={e => setPasswordForm(f => ({ ...f, new_password: e.target.value }))} required minLength={8} className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#059669]" data-testid="new-password-input" />
            </div>
            <button type="submit" disabled={saving} className="px-5 py-2 bg-[#0F172A] text-white text-sm font-medium rounded-lg hover:bg-[#1E293B] disabled:opacity-60" data-testid="change-password-btn">
              {saving ? "Changing..." : "Change Password"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
